import { useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * The outcome panels from the canvas (1j-1p), driven by the `outcome` query
 * parameter the callback redirects back with.
 *
 * The admin-consent case is the one that decides whether someone can use the
 * product at all, so it carries three things: what to ask for, a link that
 * lands the administrator in their own directory, and the alternative that
 * does not require approving the app organisation-wide.
 */

export interface MicrosoftConsentInfo {
  clientId: string;
  redirectUri: string;
}

function adminConsentUrl(domain: string, info: MicrosoftConsentInfo): string {
  const tenant = domain.trim().toLowerCase() || "organizations";
  const params = new URLSearchParams({
    client_id: info.clientId,
    redirect_uri: info.redirectUri,
  });
  return `https://login.microsoftonline.com/${encodeURIComponent(tenant)}/adminconsent?${params.toString()}`;
}

function AdminConsentPanel({
  info,
  onDismiss,
}: {
  info: MicrosoftConsentInfo | undefined;
  onDismiss: () => void;
}) {
  const [domain, setDomain] = useState("");
  const link = info ? adminConsentUrl(domain, info) : "";

  const request = [
    "Hello,",
    "",
    "I'd like to connect my work calendar to Meet With, which finds free time across the calendars I use so people I invite can request meetings.",
    "",
    "It asks for one delegated permission: Calendars.ReadBasic — read-only access to calendar names and event start and end times. It cannot create, edit, move or delete events, cannot read event subjects, bodies, attendees or attachments, and cannot send mail. It requests no application permissions, so it has no tenant-wide access to anyone else's calendar.",
    "",
    "There are two ways to allow it:",
    "",
    "1. Grant admin consent once for the organisation:",
    link ? `   ${link}` : "",
    "",
    "2. Or, if you'd rather not approve it organisation-wide, add Calendars.ReadBasic to the Low permission classification under Entra ID > Enterprise apps > Consent and permissions > Permission classifications. Users can then consent for their own calendar only.",
    "",
    "Thank you.",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div className="notice notice-warn" role="alert">
      <strong>Your organization needs an administrator to approve this</strong>
      <span>
        Nothing was connected, and nothing about your calendar was read. Organizations
        that restrict app consent need an administrator to allow it once — after that,
        connecting works normally.
      </span>

      <label className="field">
        <span>Your work email domain</span>
        <input
          type="text"
          placeholder="contoso.com"
          value={domain}
          onChange={(event) => setDomain(event.target.value)}
        />
        <span className="helper">
          Used only to build the approval link so your administrator lands in your own
          directory.
        </span>
      </label>

      <textarea className="request" readOnly rows={9} value={request} />

      <div className="row">
        <button
          type="button"
          className="ghost"
          onClick={() => void navigator.clipboard?.writeText(request)}
        >
          Copy the request
        </button>
        {info ? (
          <a className="ghost" href={link} target="_blank" rel="noreferrer">
            Open the approval link
          </a>
        ) : null}
        <button type="button" className="ghost" onClick={onDismiss}>
          Dismiss
        </button>
      </div>

      <span className="helper">
        Some organizations also let you request approval from Microsoft&rsquo;s own prompt
        — if you saw a &ldquo;Request approval&rdquo; option there, that reaches your
        administrator too.
      </span>
    </div>
  );
}

const PANELS: Record<string, { title: string; detail: string }> = {
  blocked_by_policy: {
    title: "Your organization's policy blocked this",
    detail:
      "A Conditional Access rule stopped the connection — often a managed-device or network requirement. Trying from your work device usually resolves it. Nothing is connected, and nothing about your calendar was read.",
  },
  expired: {
    title: "That connection needs setting up again",
    detail:
      "The previous authorization is no longer valid — usually after a password change or a revoked session. Reconnecting restores it; your calendar selections are kept.",
  },
  declined: {
    title: "The connection was cancelled",
    detail:
      "Nothing happened, and nothing was connected. You can try again whenever you like.",
  },
  state: {
    title: "That attempt expired",
    detail: "Connection attempts stay valid for 30 minutes. Please start again.",
  },
  limit: {
    title: "That's as many accounts as this version supports",
    detail:
      "Meet With connects up to five Microsoft 365 accounts and two Google accounts. Disconnect one you no longer need to add another.",
  },
  failed: {
    title: "We couldn't complete the connection",
    detail: "Something went wrong between here and your provider. Nothing was connected.",
  },
};

export function ConnectionOutcome({
  microsoft,
}: {
  microsoft: MicrosoftConsentInfo | undefined;
}) {
  const [params, setParams] = useSearchParams();
  const outcome = params.get("outcome");

  const dismiss = () => {
    const next = new URLSearchParams(params);
    next.delete("outcome");
    next.delete("consentUrl");
    next.delete("provider");
    next.delete("account");
    setParams(next, { replace: true });
  };

  if (!outcome || outcome === "connected") return null;

  if (outcome === "needs_admin_consent") {
    return <AdminConsentPanel info={microsoft} onDismiss={dismiss} />;
  }

  const panel = PANELS[outcome] ?? PANELS["failed"]!;

  return (
    <div className="notice notice-error" role="alert">
      <strong>{panel.title}</strong>
      <span>{panel.detail}</span>
      <div className="row">
        <button type="button" className="ghost" onClick={dismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
